"use client";

import React, { useEffect, useState } from "react";
import { Menu, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-2">
        <div className="h-9 w-24 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={true} className="hidden md:flex z-50">
      <NavigationMenuList>
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:text-secondary data-[state=open]:text-secondary text-foreground font-default font-medium transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-card border-border rounded-lg shadow-lg">
                    {item.children.map(child => <li key={child.id}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-secondary focus:bg-accent focus:text-secondary">
                            <div className="text-sm font-default font-medium leading-none text-foreground">{child.label}</div>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:text-secondary text-foreground font-default font-medium transition-colors")}>
                <Link to={item.href || "#"}>{item.label}</Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map(item => <div key={item.id} className="flex flex-col gap-3">
          <Link to={item.href || "#"} className="text-lg font-default font-semibold text-foreground hover:text-secondary transition-colors">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="flex flex-col gap-2 pl-4 border-l border-border">
              {item.children.map(child => <Link key={child.id} to={child.href || "#"} className="text-muted-foreground hover:text-secondary transition-colors py-1 font-default">
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
    </div>;
}
export default function ProductionHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="264758" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : "border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between gap-4 md:gap-8">
        
        <div className="flex items-center gap-8">
          <Link to="/HomePage" className="flex-shrink-0 flex items-center gap-2 group">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png-wvc-sizes" alt="Logo" className="h-10 w-auto transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" src="https://master-cub.10web.cloud/wp-content/uploads/2026/05/SolidGreen_logo_trasparente.png" />
            
          </Link>
          
          <MenuProvider menu_id="45">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-4">
          
          <div className="relative hidden lg:block w-64 xl:w-80">
            <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Cerca prodotti..." className="w-full bg-muted/30 border-border focus-within:border-secondary transition-colors rounded-lg font-default" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-2">
              <kbd className="hidden xl:inline-flex items-center gap-1 rounded border border-border bg-muted/50 px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                <span className="text-xs"></span>K
              </kbd>
            </div>
          </div>

          <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-secondary transition-colors">
            <Search className="size-5" />
            <span className="sr-only">Cerca</span>
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-secondary hover:bg-accent/50 transition-colors">
                <Menu className="size-6" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
              <SheetTitle className="sr-only">Menu di Navigazione</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-start pb-6 border-b border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/Allineamento testo ITALIA al centro.png-wvc-sizes" alt="Logo" className="h-8 w-auto" loading="lazy" decoding="async" />
                </div>
                <div className="flex-1 overflow-y-auto py-4">
                  <MenuProvider menu_id="45">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}